import {Component, effect, inject, Injector, signal, ChangeDetectionStrategy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {Dispatcher} from '@ngrx/signals/events';
import {ReviewsStore} from '@core/stores/reviews.store';
import {form, FormField, required} from '@angular/forms/signals';
import {MatFormField, MatLabel} from '@angular/material/input';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {MatButton} from '@angular/material/button';
import {tagReviewWithLabel} from '@core/events/reviews.events';
import {MatProgressBar} from '@angular/material/progress-bar';


export type TagFormDialogData = {
  review: {
    id?: string;
    labels?: TagLabel[];
  };
};

export type TagFormDialogResult = {
  submitted: boolean;
};

type TagLabel = {
  id?: number | string;
  name?: string;
};

// Local payload shape avoids compile coupling to generated model exports.
type PutReviewLabelsPayload = {
  reviewId: string;
  labelIds: number[];
};

type TagFormModel = {
  reviewId: string;
  labelIds: number[];
};

@Component({
  selector: 'app-tag-form',
  templateUrl: './tag-form.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    FormField,
    MatButton,
    MatDialogActions,
    MatProgressBar

  ]
})
export class TagFormComponent{

  readonly dialogRef = inject<MatDialogRef<TagFormComponent, TagFormDialogResult>>(MatDialogRef);
  readonly dispatcher = inject(Dispatcher);
  private readonly reviewsStore = inject(ReviewsStore);
  readonly dialogData = inject<TagFormDialogData>(MAT_DIALOG_DATA);
  private readonly injector = inject(Injector);
  readonly loadingTagForm = this.reviewsStore.loadingTagForm;
  private seenTagVersion = this.reviewsStore.tagReviewWithLabelVersion();

  updateTag = signal<TagFormModel>({
    reviewId: this.dialogData.review.id ?? '',
    labelIds: (this.dialogData.review.labels ?? [])
      .map((label: TagLabel) => Number(label.id))
      .filter((id: number) => Number.isFinite(id))
  });

  get labels(): TagLabel[] {
    return this.reviewsStore.labels() as TagLabel[];
  }

  get errorMessage(): string | undefined {
    return this.reviewsStore.tagReviewWithLabelError();
  }

  updateForm = form(this.updateTag, (schemaPath) => {
    required(schemaPath.reviewId);
    required(schemaPath.labelIds);
  });

  constructor() {
    effect(() => {
      const version = this.reviewsStore.tagReviewWithLabelVersion();
      if (version !== this.seenTagVersion) {
        this.seenTagVersion = version;
        // Close in the next microtask to avoid changing bound values during the
        // current change-detection cycle.
        Promise.resolve().then(() => this.dialogRef.close({ submitted: true }));
      }
    }, { injector: this.injector });
  }

  save() {
    const value = this.updateForm().value() as TagFormModel;
    const payload: PutReviewLabelsPayload = {
      reviewId: this.dialogData.review.id ?? value.reviewId ?? '',
      labelIds: (value.labelIds ?? [])
        .map((id: number) => Number(id))
        .filter((id: number) => Number.isFinite(id))
    };

    this.dispatcher.dispatch(tagReviewWithLabel(payload));
  }

  close() {
    this.dialogRef.close({ submitted: false });
  }
}
